/**
 * Which languages an article may be written in.
 *
 * The set belongs to the CASINO, not to the studio: an article's `language` is
 * content metadata (it drives the public page's `lang` attribute and the
 * hreflang links between translations), so the only defensible source is the
 * brand's own supported set. That arrives on the blog tenant row as
 * `details.languages` — written by the owner-hub control plane at casino
 * creation, in the same vocabulary the articles already store.
 *
 * Deliberately NOT sourced from the studio's own locale machinery:
 *
 * - `window.config.languages` is the OWNERS-HUB portal's set, not the casino's.
 *   It is also in APP codes, while the casino's set is in BACKEND codes — the
 *   two spell three markets differently (`bra`/`arg`/`za` vs `br`/`ar`/`af`),
 *   which is what `config/langCodeMap.ts` exists to convert.
 * - `availableLocales.scopedLanguages()` narrows to the locales THIS build ships
 *   a UI translation for. An article may perfectly well be written in a language
 *   the admin UI is not translated into — narrowing there would silently drop
 *   Romanian from a Romanian casino.
 */

/**
 * What a casino with no languages on record gets: exactly the pair that used to
 * be hardcoded here. A tenant row predating the `details` blob therefore behaves
 * as it always did, rather than collapsing to an English-only picker.
 */
export const ARTICLE_LANGUAGE_FALLBACK: readonly string[] = ['en', 'mx'];

/**
 * The language codes to offer, in the order they should appear.
 *
 * The casino's own list wins and keeps the control plane's order (the market's
 * primary language first). `currentLanguage` — the language the article is
 * already saved as — is appended when the casino does not list it, so an
 * existing translation never renders as a blank `Select` that the next save
 * would silently rewrite.
 */
export const articleLanguageCodes = (
  tenantLanguages: readonly string[],
  currentLanguage: string,
): string[] => {
  const codes = tenantLanguages.length > 0 ? [...tenantLanguages] : [...ARTICLE_LANGUAGE_FALLBACK];

  const current = currentLanguage.trim().toLowerCase();
  if (current.length > 0 && !codes.some((code) => code.toLowerCase() === current)) {
    codes.push(current);
  }

  return codes;
};
